/**
 * The upgrade screen: what this character is, and what a point would buy.
 *
 * A component rather than a screen, returning a root the way `saveTransferPanel` does, so the hub
 * in #28 hosts exactly this and the main menu hosts it today. Nothing here decides a rule: every
 * number on screen comes out of `sim/progression`, and every refusal is worded from the failure
 * that module raised. The before and after column is `previewPerk`, which builds the character
 * twice and subtracts, so the figure a player reads is the figure the simulation will use.
 *
 * Rebuilt wholesale on every change rather than patched. The tree is a few dozen nodes and the
 * panel is only open in a menu, so the cost is nothing next to keeping two representations of a
 * character in step. That is the same call `appShell` makes about the menu itself.
 */

import {
  STAT_LABELS,
  UPGRADE_LEVEL_CAPPED,
  UPGRADE_NO_SELECTION,
  UPGRADE_PERK_POINTS,
  UPGRADE_RESPEC,
  UPGRADE_RESPEC_CANCEL,
  UPGRADE_RESPEC_CONFIRM,
  UPGRADE_RESPEC_WARNING,
  UPGRADE_STAT_ORDER,
  UPGRADE_STAT_POINTS,
  upgradeAtLastRank,
  upgradeCompareLabel,
  upgradeCompareValue,
  upgradeLevel,
  upgradePoints,
  upgradeXpToNext,
} from '@/text/upgradeText';
import { MAX_STAT, statValue } from '@/sim/combat/statContract';
import { PERK_BRANCH_ORDER, perkBranch } from '@/sim/progression/perkBranchRegistry';
import { PERK_ORDER, perk, perksInBranch } from '@/sim/progression/perkRegistry';
import {
  levelProgress,
  xpIntoLevel,
  xpAcrossLevel,
  LEVEL_CAP,
} from '@/sim/progression/experienceCurve';
import {
  perkAvailable,
  progressLevel,
  rankOf,
  respec,
  spendStatPoint,
  takePerk,
  unspentPerkPoints,
  unspentStatPoints,
} from '@/sim/progression/progressState';
import { previewPerk } from '@/sim/progression/perkPreview';
import { el, setText } from '@/ui/dom';
import { injectUpgradeStyles } from '@/ui/progression/upgradeStyles';
import { progressionRefusal } from '@/text/progressionText';
import type { PerkId } from '@/sim/progression/perkContract';
import type { PreviewChange } from '@/sim/progression/perkPreview';
import type { ProfileSession } from '@/save/profileSession';
import type { StatId } from '@/sim/combat/statContract';

export interface UpgradePanel {
  readonly root: HTMLElement;
  dispose(): void;
}

export interface UpgradePanelOptions {
  readonly profile: ProfileSession;
}

export function createUpgradePanel(options: UpgradePanelOptions): UpgradePanel {
  const removeStyles = injectUpgradeStyles();
  const { profile } = options;

  const root = el('div', 'upgrade');
  const status = el('div', 'upgrade-status');
  status.setAttribute('role', 'status');

  // Which node the before and after column is describing. Cleared by anything that changes the
  // character, because a preview of a rank that has just been bought is a preview of the past.
  let selected: PerkId | null = null;
  let confirmingRespec = false;

  const say = (message: string): void => {
    setText(status, message);
  };

  const rebuild = (): void => {
    root.replaceChildren(...sections());
    root.append(status);
  };

  const sections = (): readonly HTMLElement[] => {
    const saved = profile.current();
    const { progress, stats } = saved;
    const level = progressLevel(progress);
    const statPoints = unspentStatPoints(progress, stats);
    const perkPoints = unspentPerkPoints(progress);

    // No heading of its own: this panel only ever mounts inside a sheet that is already titled
    // "Character", and two of the same word stacked is what made the menu read as crowded.
    return [
      levelRow(level, progress.xp),
      bar(levelProgress(progress.xp)),
      pointsRow(statPoints, perkPoints),
      statGrid(statPoints),
      tree(perkPoints),
      compare(),
      actions(),
    ];
  };

  const levelRow = (level: number, xp: number): HTMLElement => {
    const row = el('div', 'upgrade-level');
    row.append(el('span', 'upgrade-level-number', upgradeLevel(level)));
    row.append(
      el(
        'span',
        'upgrade-xp',
        level >= LEVEL_CAP
          ? UPGRADE_LEVEL_CAPPED
          : upgradeXpToNext(xpIntoLevel(xp), xpAcrossLevel(xp)),
      ),
    );
    return row;
  };

  const bar = (fraction: number): HTMLElement => {
    const track = el('div', 'upgrade-bar');
    const fill = el('div', 'upgrade-bar-fill');
    fill.style.width = `${String(Math.round(fraction * 100))}%`;
    track.append(fill);
    return track;
  };

  const pointsRow = (statPoints: number, perkPoints: number): HTMLElement => {
    const row = el('div', 'upgrade-points');
    row.append(pointsLabel(UPGRADE_STAT_POINTS, statPoints));
    row.append(pointsLabel(UPGRADE_PERK_POINTS, perkPoints));
    return row;
  };

  const pointsLabel = (name: string, count: number): HTMLElement => {
    const live = count > 0 ? ' upgrade-points-live' : '';
    return el('span', `upgrade-points-item${live}`, upgradePoints(name, count));
  };

  const statGrid = (available: number): HTMLElement => {
    const grid = el('div', 'upgrade-stats');
    const saved = profile.current();

    for (const id of UPGRADE_STAT_ORDER) {
      const value = statValue(saved.stats, id);
      const button = el('button', 'upgrade-stat');
      button.type = 'button';
      button.disabled = available < 1 || value >= MAX_STAT;
      button.append(el('span', 'upgrade-stat-name', STAT_LABELS[id]));
      button.append(
        el('span', 'upgrade-stat-value', `${String(value)}${button.disabled ? '' : ' +'}`),
      );
      button.addEventListener('click', () => {
        spendStat(id);
      });
      grid.append(button);
    }

    return grid;
  };

  const tree = (available: number): HTMLElement => {
    const grid = el('div', 'upgrade-tree');

    for (const id of PERK_BRANCH_ORDER) {
      const branch = perkBranch(id);
      const column = el('div', 'upgrade-branch');
      column.append(el('div', 'upgrade-branch-title', branch.title));
      column.append(el('p', 'upgrade-branch-blurb', branch.blurb));

      for (const def of perksInBranch(id)) column.append(node(def.id, available));

      grid.append(column);
    }

    return grid;
  };

  const node = (id: PerkId, available: number): HTMLElement => {
    const def = perk(id);
    const saved = profile.current();
    const rank = rankOf(saved.progress, id);
    const check = perkAvailable(saved.progress, id);
    const affordable = check.ok;
    const taken = rank > 0;

    const button = el(
      'button',
      `upgrade-perk${affordable ? ' is-affordable' : ''}${taken ? ' is-taken' : ''}`,
    );
    button.type = 'button';
    // Never disabled while there is something to read: selecting a node is how a player finds out
    // what it would do and why they cannot have it, and a disabled button says neither.
    button.disabled = false;

    const head = el('div', 'upgrade-perk-head');
    head.append(el('span', 'upgrade-perk-title', def.title));
    head.append(el('span', 'upgrade-perk-rank', `${String(rank)} / ${String(def.maxRanks)}`));
    button.append(head);
    button.append(el('p', 'upgrade-perk-blurb', def.blurb));

    if (!affordable && available > 0) {
      button.append(el('span', 'upgrade-perk-why', progressionRefusal(check.failure)));
    }

    button.addEventListener('click', () => {
      if (affordable) {
        buyPerk(id);
        return;
      }
      selected = selected === id ? null : id;
      rebuild();
    });

    return button;
  };

  const compare = (): HTMLElement => {
    const box = el('div', 'upgrade-compare');

    if (selected === null) {
      setText(box, UPGRADE_NO_SELECTION);
      return box;
    }

    const saved = profile.current();
    const changes = previewPerk(
      { stats: saved.stats, progress: saved.progress },
      saved.inventory.equipment,
      selected,
    );

    if (changes.length === 0) {
      setText(box, upgradeAtLastRank(perk(selected).title));
      return box;
    }

    for (const change of changes) box.append(compareRow(change));

    return box;
  };

  const compareRow = (change: PreviewChange): HTMLElement => {
    const row = el('div', 'upgrade-compare-row');
    row.append(el('span', 'upgrade-compare-label', upgradeCompareLabel(change.label, change.when)));
    const verdict = change.better ? 'upgrade-compare-better' : 'upgrade-compare-worse';

    row.append(
      el(
        'span',
        `upgrade-compare-value ${verdict}`,
        upgradeCompareValue(format(change.before), format(change.after)),
      ),
    );
    return row;
  };

  const actions = (): HTMLElement => {
    const row = el('div', 'upgrade-actions');

    if (!confirmingRespec) {
      row.append(
        button(UPGRADE_RESPEC, 'upgrade-button is-danger', () => {
          confirmingRespec = true;
          say(UPGRADE_RESPEC_WARNING);
          rebuild();
        }),
      );
      return row;
    }

    row.append(button(UPGRADE_RESPEC_CONFIRM, 'upgrade-button is-danger', doRespec));
    row.append(
      button(UPGRADE_RESPEC_CANCEL, 'upgrade-button', () => {
        confirmingRespec = false;
        say('');
        rebuild();
      }),
    );
    return row;
  };

  const button = (label: string, className: string, onClick: () => void): HTMLButtonElement => {
    const element = el('button', className, label);
    element.type = 'button';
    element.addEventListener('click', onClick);
    return element;
  };

  function spendStat(id: StatId): void {
    const saved = profile.current();
    const result = spendStatPoint(saved.progress, saved.stats, id);

    if (!result.ok) {
      say(progressionRefusal(result.failure));
      return;
    }

    profile.update({ ...saved, stats: result.value });
    say(`${id} raised.`);
    selected = null;
    rebuild();
  }

  function buyPerk(id: PerkId): void {
    const saved = profile.current();
    const result = takePerk(saved.progress, id);

    if (!result.ok) {
      say(progressionRefusal(result.failure));
      return;
    }

    profile.update({ ...saved, progress: result.value });
    say(`${perk(id).title} taken.`);
    selected = null;
    rebuild();
  }

  function doRespec(): void {
    const saved = profile.current();
    const undone = respec(saved.progress);

    profile.update({ ...saved, stats: undone.stats, progress: undone.progress });
    confirmingRespec = false;
    selected = null;
    say(`Every point is back. ${String(PERK_ORDER.length)} perks to choose from again.`);
    rebuild();
  }

  rebuild();

  return {
    root,
    dispose(): void {
      root.remove();
      removeStyles();
    },
  };
}

/** A whole number stays whole, a multiplier reads as a percentage of what it started as. */
function format(value: number): string {
  if (Number.isInteger(value)) return String(value);

  return `${(value * 100).toFixed(0)}%`;
}
